import React from "react"
import { PageHeader } from "#/features/global-ui/page-header"
import { PageLayout } from "#/features/global-ui/page-layout"
import { Title } from "#/services/page-title/Title"
import { Box, ForwardIcon, GetHelpIcon, Grid, Link, Tile, useTheme } from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { Link as RouterLink } from "react-router-dom"
import { useHelpCenterCategories } from "#/services/help-center/useGetHelpCenter"
import { AdditionalTopicsLink } from "#/components/read/shared/ReadLearnLinks"
import { FullScreenLoading } from "#/components/read/shared/FullScreenLoading"
import { ErrorPage } from "#/components/views/app/ErrorPage"
import { helpCenterCategoryPath } from "#/services/help-center/urls"
import type { HelpCenterCategory, HelpCenterSubCategory } from "#/services/help-center/types"
import { usePageViewTracking } from "#/services/analytics/usePageViewTracking"
import { faqsLabelMessage } from "#/pages/faqs/messages"

const MAX_SUBCATEGORIES_PER_TILE = 3

export function Faqs(): JSX.Element {
  return <FaqsLandingPage />
}

function FaqsLandingPage(): JSX.Element {
  const intl = useIntl()
  const theme = useTheme()
  const { data: categories = [], isLoading, isError } = useHelpCenterCategories()

  usePageViewTracking({ enabled: !isLoading && !isError })

  return (
    <>
      <Title title={intl.formatMessage(faqsLabelMessage)} />
      <PageLayout
        header={
          <PageHeader
            startGlyph={<GetHelpIcon sx={{ blockSize: "2.25rem", inlineSize: "2.25rem" }} />}
            pageTitle={<FormattedMessage {...faqsLabelMessage} />}
          />
        }
      >
        {isLoading ? (
          <FullScreenLoading />
        ) : isError ? (
          <ErrorPage />
        ) : (
          <Grid
            container
            spacing={{
              xs: theme.tokens.spacing.lg,
              sm: theme.tokens.spacing.lg,
              md: theme.tokens.spacing.xl,
              lg: theme.tokens.spacing.xl
            }}
          >
            {categories.map((category) => (
              <Grid item xs={12} sm={6} md={6} lg={4} key={category.id}>
                <CategoryTile category={category} />
              </Grid>
            ))}
          </Grid>
        )}
      </PageLayout>
    </>
  )
}

function CategoryTile({ category }: { category: HelpCenterCategory }): JSX.Element {
  const categoryLink = helpCenterCategoryPath(category.slug)
  const visibleSubCategories = category.subCategories.slice(0, MAX_SUBCATEGORIES_PER_TILE)
  const additionalCount = Math.max(0, category.subCategories.length - MAX_SUBCATEGORIES_PER_TILE)

  return (
    <FaqsTile>
      <CategoryTileTitle title={category.title} categoryLink={categoryLink} />
      <SubCategoriesList categoryLink={categoryLink} subCategories={visibleSubCategories} />
      {additionalCount > 0 ? (
        <Box sx={(theme) => ({ marginInlineStart: theme.tokens.spacing.sm })}>
          <AdditionalTopicsLink categoryLink={categoryLink} additionalTopicCount={additionalCount} />
        </Box>
      ) : null}
    </FaqsTile>
  )
}

function CategoryTileTitle({ title, categoryLink }: { title: string; categoryLink: string }): JSX.Element {
  return (
    <Box sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.sm })}>
      <Link
        component={RouterLink}
        to={categoryLink}
        variant="h4"
        endIcon={<ForwardIcon fontSize="medium" color="primary" sx={{ verticalAlign: "middle" }} />}
      >
        {title}
      </Link>
    </Box>
  )
}

function SubCategoriesList({
  categoryLink,
  subCategories
}: {
  categoryLink: string
  subCategories: HelpCenterSubCategory[]
}): JSX.Element {
  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        marginBlockEnd: theme.tokens.spacing.sm,
        gap: theme.tokens.spacing.xs
      })}
    >
      {subCategories.map((subCategory) => (
        <Link
          key={subCategory.id}
          component={RouterLink}
          to={{ pathname: categoryLink, hash: subCategory.slug }}
          variant="body2"
          color="inherit"
          underline="none"
          endIcon={<ForwardIcon fontSize="small" color="primary" sx={{ verticalAlign: "middle" }} />}
        >
          {subCategory.title}
        </Link>
      ))}
    </Box>
  )
}

function FaqsTile({ children }: { children: React.ReactNode }): JSX.Element {
  const theme = useTheme()
  return <Tile sx={{ padding: theme.spacing(theme.tokens.spacing.xl), minBlockSize: "256px" }}>{children}</Tile>
}
